import { createContext, type ReactNode, useContext, useMemo } from "react";

import { ReplayDiscoveryProvider } from "@Context/game/activity/replay-discovery-context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { deriveScenarioData } from "@hooks/game/activity/session/session-results.utils";
import { ScenarioRetryProvider } from "./scenario-retry-context";
import type {
  SessionResultsCombinedModel,
  SessionResultsScenarioModel,
  SessionResultsStaticModel,
  SessionResultsTabModel,
} from "@hooks/game/activity/session/use-session-results";
import { useSessionResults } from "@hooks/game/activity/session/use-session-results";

const SessionResultsStaticContext = createContext<SessionResultsStaticModel | null>(null);
const SessionResultsTabContext = createContext<SessionResultsTabModel | null>(null);

export const useSessionResultsStaticContext = () => {
  const context = useContext(SessionResultsStaticContext);
  if (!context) {
    throw new Error("useSessionResultsStaticContext must be used within a SessionResultsPageProvider");
  }

  return context;
};

export const useSessionResultsTabContext = () => {
  const context = useContext(SessionResultsTabContext);
  if (!context) {
    throw new Error("useSessionResultsTabContext must be used within a SessionResultsPageProvider");
  }

  return context;
};

export const useSessionResultsScenarioData = (): SessionResultsScenarioModel => {
  const { sessionResults } = useSessionResultsStaticContext();
  const { currentTab } = useSessionResultsTabContext();

  return useMemo(() => deriveScenarioData(sessionResults, currentTab), [currentTab, sessionResults]);
};

export const useSessionResultsPageContext = (): SessionResultsCombinedModel => {
  const staticModel = useSessionResultsStaticContext();
  const tabModel = useSessionResultsTabContext();
  const { scenarioData, targetOutcome } = useSessionResultsScenarioData();

  return useMemo(
    () => ({
      ...staticModel,
      ...tabModel,
      scenarioData,
      targetOutcome,
    }),
    [scenarioData, staticModel, tabModel, targetOutcome]
  );
};

interface ISessionResultsPageProviderProps {
  children: ReactNode;
  headerButtonType: VRHeaderButtonType;
}

export const SessionResultsPageProvider = ({ children, headerButtonType }: ISessionResultsPageProviderProps) => {
  const pageModel = useSessionResults({ headerButtonType });
  const { staticModel, tabModel } = pageModel;

  return (
    <SessionResultsStaticContext.Provider value={staticModel}>
      <SessionResultsTabContext.Provider value={tabModel}>
        <ReplayDiscoveryProvider>
          <ScenarioRetryProvider userId={staticModel.userId}>{children}</ScenarioRetryProvider>
        </ReplayDiscoveryProvider>
      </SessionResultsTabContext.Provider>
    </SessionResultsStaticContext.Provider>
  );
};
